import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { reportText as tr } from '../utils/reportTranslation';
import Header from './Header';
import ClinicalFindings from './ClinicalFindings';
import ParentInfo from './ParentInfo';
import ReportActions from './ReportActions';
// Send to EHR is temporarily disabled while the permanent-record workflow is defined.
// import ShareToMyChart from './ShareToMyChart';
import LoadingSpinner from './LoadingSpinner';
import ErrorDisplay from './ErrorDisplay';
// Clinician survey is temporarily hidden.
// import ClinicianSurveyModal from './ClinicianSurveyModal';
import CheckInTimeline from './CheckInTimeline';
import ReferenceReport from './ReferenceReport';
import api, { deleteClinicalFinding, requestParentFollowUp, saveClinicalFinding } from '../services/api';
import type { ReportData, CheckInData, ClinicalFinding } from '../types';

interface ReportViewerProps {
  shareId: string;
  mockData?: ReportData;
  bypassApi?: boolean;
  viewerType?: string;
  accessCode?: string;
}

/**
 * Main report viewer component
 *  constructing a comprehensive information system
 */
const ReportViewer: React.FC<ReportViewerProps> = ({ shareId, mockData, bypassApi = false, viewerType, accessCode }) => {
  useTranslation('report');
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [chainCheckIns, setChainCheckIns] = useState<CheckInData[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chainLoadWarning, setChainLoadWarning] = useState<string | null>(null);
  const [sessionExtended, setSessionExtended] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  /*
  Clinician survey is temporarily hidden.
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyStatus, setSurveyStatus] = useState<ClinicianSurveyStatus>({
    exists: false,
    isComplete: false,
    dismissedForLater: false,
    dismissCount: 0
  });
  const [surveyPopupShown, setSurveyPopupShown] = useState(false);
  */
  
  // Determine if this is a provider/doctor viewer
  const isProvider = viewerType?.toLowerCase() === 'provider' || 
                     viewerType?.toLowerCase() === 'doctor' || 
                     viewerType?.toLowerCase() === 'd';
  const isStudyCoordinator = viewerType?.toLowerCase() === 'studycoordinator' ||
                             viewerType?.toLowerCase() === 'study_coordinator' ||
                             viewerType?.toLowerCase() === 'sc' ||
                             reportData?.privacyMode === 'studyCoordinator';

  /*
  Clinician survey is temporarily hidden.
  useEffect(() => {
    if (!isProvider || bypassApi) return;

    const fetchSurveyStatus = async () => {
      try {
        const status = await getClinicianSurveyStatus(shareId);
        setSurveyStatus(status);
      } catch (err) {
        console.error('Failed to fetch survey status:', err);
      }
    };

    fetchSurveyStatus();
  }, [shareId, isProvider, bypassApi]);

  useEffect(() => {
    if (!isProvider || bypassApi || loading || surveyPopupShown || surveyStatus.isComplete || !reportData) return;

    const delayMs = hasFollowUpVideos ? 1200 : 45000;
    const timer = window.setTimeout(() => {
      if (!surveyStatus.isComplete) {
        setShowSurveyModal(true);
        setSurveyPopupShown(true);
      }
    }, delayMs);

    return () => window.clearTimeout(timer);
  }, [isProvider, bypassApi, loading, surveyPopupShown, surveyStatus.isComplete, hasFollowUpVideos, reportData]);

  const handleSurveyModalClose = useCallback(() => {
    setShowSurveyModal(false);
  }, []);

  const handleSurveyDismissForLater = useCallback(() => {
    setShowSurveyModal(false);
    setSurveyStatus(prev => ({ ...prev, dismissedForLater: true, dismissCount: prev.dismissCount + 1 }));
  }, []);

  const handleSurveyComplete = useCallback(() => {
    setShowSurveyModal(false);
    setSurveyStatus(prev => ({ ...prev, isComplete: true }));
  }, []);

  const handleOpenSurvey = useCallback(() => {
    setShowSurveyModal(true);
    setSurveyPopupShown(true);
  }, []);
  */

  const handleSaveClinicalFinding = useCallback(async (notes: string): Promise<ClinicalFinding> => {
    if (bypassApi) {
      const now = new Date().toISOString();
      const clinicalFinding: ClinicalFinding = {
        id: reportData?.clinicalFinding?.id ?? 'demo-clinical-finding',
        notes,
        createdAt: reportData?.clinicalFinding?.createdAt ?? now,
        updatedAt: now,
      };

      setReportData((current) => current ? { ...current, clinicalFinding } : current);
      return clinicalFinding;
    }

    const response = await saveClinicalFinding(shareId, { notes });
    setReportData((current) => current ? { ...current, clinicalFinding: response.clinicalFinding } : current);
    return response.clinicalFinding;
  }, [bypassApi, reportData?.clinicalFinding?.createdAt, reportData?.clinicalFinding?.id, shareId]);

  const handleDeleteClinicalFinding = useCallback(async (): Promise<void> => {
    if (!bypassApi) {
      await deleteClinicalFinding(shareId);
    }

    setReportData((current) => current ? { ...current, clinicalFinding: null } : current);
  }, [bypassApi, shareId]);

  const handleRequestParentFollowUp = useCallback(async (): Promise<void> => {
    if (bypassApi) {
      return;
    }

    await requestParentFollowUp(shareId);
  }, [bypassApi, shareId]);

  // Session management (skip in demo)
  useEffect(() => {
    if (bypassApi) return;
    let cancelled = false;

    const checkAndExtend = async () => {
      try {
        const isValid = await api.checkShareSession(shareId);
        if (!isValid) {
          if (!cancelled) setError('Your session has expired. Please use the link from your email to access the report again.');
          return;
        }
        // Always extend on activity tick
        await api.extendShareSession(shareId);
        if (!cancelled) setSessionExtended(true);
      } catch (err) {
        console.error('Session check/extend failed:', err);
      }
    };

    // Initial extend immediately on mount
    checkAndExtend();

    // Keep-alive every 2 minutes
    const interval = setInterval(checkAndExtend, 2 * 60 * 1000);

    // Activity-based keep-alive (debounced)
    let activityTimeout: number | null = null;
    const onActivity = () => {
      if (activityTimeout) window.clearTimeout(activityTimeout);
      activityTimeout = window.setTimeout(checkAndExtend, 30 * 1000);
    };
    document.addEventListener('mousemove', onActivity);
    document.addEventListener('keydown', onActivity);
    document.addEventListener('scroll', onActivity, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkAndExtend();
    });

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('mousemove', onActivity);
      document.removeEventListener('keydown', onActivity);
      document.removeEventListener('scroll', onActivity);
    };
  }, [shareId, bypassApi]);

  // Fetch report data (or inject mock)
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        setChainLoadWarning(null);

        if (bypassApi && mockData) {
          setReportData(mockData);
          if (mockData.chainData && mockData.chainData.length > 0) {
            setChainCheckIns(mockData.chainData);
          } else {
            setChainCheckIns([mockData.checkInData]);
          }
          setActiveIdx(0);
          return;
        }

        // Log access
        await api.logShareAccess(shareId, 'report_viewed');

        // Fetch report data
        const data = await api.fetchReportData(shareId);
        setReportData(data);
        if (data.chainDataIncomplete) {
          setChainLoadWarning(
            'Related eChecks could not be loaded. This report is showing only the selected eCheck.',
          );
        }

        // Initialize chain check-ins. Prefer the chain payload included on the
        // report response; otherwise fall back to the single check-in.
        let chain: CheckInData[] = [];
        if (data.chainData && data.chainData.length > 0) {
          chain = data.chainData;
        } else if (data.checkInData?.eCheckChain) {
          // The report's check-in advertises a chain but the chain wasn't inlined —
          // attempt the dedicated endpoint as a graceful enhancement.
          try {
            const remoteChain = await api.fetchChainData(shareId);
            chain = remoteChain.length > 0 ? remoteChain : [data.checkInData];
          } catch {
            chain = [data.checkInData];
            setChainLoadWarning(
              'Related eChecks could not be loaded. This report is showing only the selected eCheck.',
            );
          }
        } else {
          chain = [data.checkInData];
        }
        setChainCheckIns(chain);
        setActiveIdx(0);

        // AccessCount is incremented during validation, so equality means this is
        // the final allowed load rather than an already-expired share.
        if (data.maxAccessCount && data.accessCount > data.maxAccessCount) {
          setError('This report has reached its maximum number of views.');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'We could not load this report. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [shareId, bypassApi, mockData]);

  // Auto-dismiss the session extended toast after 1 second
  useEffect(() => {
    if (!sessionExtended) return;
    const timer = window.setTimeout(() => setSessionExtended(false), 1000);
    return () => window.clearTimeout(timer);
  }, [sessionExtended]);

  useEffect(() => {
    const updateBackToTopVisibility = () => setShowBackToTop(window.scrollY > 480);
    updateBackToTopVisibility();
    window.addEventListener('scroll', updateBackToTopVisibility, { passive: true });
    return () => window.removeEventListener('scroll', updateBackToTopVisibility);
  }, []);

  // Parent notification links target the provider note. Because report data is
  // loaded asynchronously, repeat the browser's fragment navigation after the
  // note has actually been mounted.
  useEffect(() => {
    if (loading || !reportData || isProvider || window.location.hash !== '#provider-note') return;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById('provider-note')?.scrollIntoView({ block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isProvider, loading, reportData]);

  // Handle PDF download
  const handleDownload = useCallback(async () => {
    if (!reportData?.reportPdfUrl) return;

    try {
      if (!bypassApi) {
        // Log download action
        await api.logShareAccess(shareId, 'report_downloaded');

        // Download the PDF from API
        const blob = await api.downloadReport(shareId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `MamaBear_Report_${reportData.childInfo.name}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // In demo mode, just open the sample PDF URL
        window.open(reportData.reportPdfUrl, '_blank');
      }
    } catch (err) {
      console.error('Download failed:', err);
      alert(tr('We could not download this report. Please try again.'));
    }
  }, [shareId, reportData, bypassApi]);

  const handleViewReport = useCallback(() => {
    document.getElementById('report-content')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  const handleBackToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSelectCheckIn = useCallback((index: number) => {
    setActiveIdx(index);
    window.requestAnimationFrame(() => {
      document.getElementById(`echeck-${index + 1}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  // Handle action button clicks
  const handleInsuranceInfo = () => {
    window.open('https://mamabearhealth.com/home/for-providers/insurance-reimbursement-cpt-codes/?hilite=insurance-reimbursement-cpt-codes', '_blank', 'noopener,noreferrer');
  };

  const handleMedicalNecessity = () => {
    window.open('https://mamabearhealth.com/home/for-providers/?hilite=Medical+Necessity+Language', '_blank', 'noopener,noreferrer');
  };

  const handleWorkflowInfo = () => {
    window.open('https://mamabearhealth.com/rhtp-readiness/?hilite=workflow', '_blank', 'noopener,noreferrer');
  };

  // Loading state: show page shell with loader over the inline report area
  if (loading) {
    return (
      <div className="min-h-screen">
        <Header childName="" reportDate="" />
        <main className="report-frame report-main">
          <div className="report-card report-card--tight report-loading-card">
            <div className="flex items-center justify-center p-10">
              <LoadingSpinner message={tr('Loading secure report...')} />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error || !reportData) {
    return <ErrorDisplay error={tr(error || 'Report not found')} fullScreen />;
  }

  // Keep the timeline selected state while the report itself renders every eCheck in sequence.
  const hasFollowUpReports = chainCheckIns.length > 1;
  const reportCheckIns: CheckInData[] = chainCheckIns.length > 0
    ? chainCheckIns
    : [reportData.checkInData];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <Header
        childName={reportData.childInfo.name}
        reportDate={(reportData.checkInData as any).dateDisplayServer ?? reportData.checkInData.date}
        childAge={reportData.childInfo.age}
        childDateOfBirth={reportData.childInfo.dateOfBirth}
        childGender={reportData.childInfo.gender}
        childInsuranceStatus={reportData.childInfo.insuranceStatus}
        viewCount={reportData.viewCount}
        accessCount={reportData.accessCount}
        onViewReport={handleViewReport}
        canDownloadReport={Boolean(reportData.reportPdfUrl)}
        onDownloadReport={reportData.reportPdfUrl ? handleDownload : undefined}
        showProviderNote={isProvider || Boolean(reportData.clinicalFinding?.notes?.trim())}
        showCaregiverDetails={!isStudyCoordinator || Boolean(reportData.parentInfo.communityType)}
        renderBelowNav={hasFollowUpReports ? (
          <CheckInTimeline
            checkIns={chainCheckIns}
            activeIdx={activeIdx}
            onSelect={handleSelectCheckIn}
          />
        ) : undefined}
      />

      {chainLoadWarning && (
        <div className="report-frame" style={{ paddingTop: 16, paddingBottom: 0 }}>
          <div role="alert" className="report-card" style={{ borderColor: '#F59E0B', color: '#92400E', padding: '12px 14px' }}>
            {tr(chainLoadWarning)}
          </div>
        </div>
      )}

      <div className={isStudyCoordinator ? 'study-coordinator-workspace' : undefined}>
        <ReferenceReport
          reportTitle={reportData.reportTitle}
          childInfo={reportData.childInfo}
          checkIns={reportCheckIns}
          videos={reportData.videos ?? []}
          accessCode={accessCode}
          reportUrl={typeof window !== 'undefined' ? window.location.href : ''}
          isStudyCoordinator={isStudyCoordinator}
          onNavigateToECheck={handleSelectCheckIn}
        />

        {/* Web-only content: intentionally excluded from the generated PDF. */}
        <main id="report-content" className="report-frame report-main">
          <div className={`report-grid${isStudyCoordinator ? ' report-grid--single' : ''}`}>
            <div className="report-column">
              <ClinicalFindings
                clinicalFinding={reportData.clinicalFinding}
                isProvider={isProvider}
                onSave={handleSaveClinicalFinding}
                onDelete={handleDeleteClinicalFinding}
                onRequestFollowUp={isProvider ? handleRequestParentFollowUp : undefined}
              />
            </div>

            <div className="report-column">
              {(!isStudyCoordinator || reportData.parentInfo.communityType) && (
                <ParentInfo
                  parentInfo={reportData.parentInfo}
                  communityTypeOnly={isStudyCoordinator}
                />
              )}

              <ReportActions
                onInsuranceInfo={handleInsuranceInfo}
                onMedicalNecessity={handleMedicalNecessity}
                onWorkflowInfo={handleWorkflowInfo}
              />

              {/* Send to EHR is commented out while the record-retention workflow is being defined.
              {!isProvider && (
                <ShareToMyChart
                  reportUrl={typeof window !== 'undefined' ? window.location.href : ''}
                  accessCode={accessCode}
                  isParentViewer={!isProvider}
                  onError={(message) => console.error('MyChart share failed:', message)}
                />
              )}
              */}
            </div>
          </div>
        </main>
      </div>

      {/*
      Clinician Survey Modal is temporarily hidden.
      {isProvider && (
        <ClinicianSurveyModal
          shareId={shareId}
          isOpen={showSurveyModal}
          onClose={handleSurveyModalClose}
          onDismissForLater={handleSurveyDismissForLater}
          onComplete={handleSurveyComplete}
        />
      )}
      */}

      {/* Session Warning */}
      {sessionExtended && (
        <div className="fixed bottom-4 right-4 rounded-full border border-[rgba(23,96,63,0.18)] bg-[var(--green-soft)] px-4 py-3 text-[var(--green-text)] shadow-lg">
          <p className="text-sm">{tr('Your session has been extended due to activity.')}</p>
        </div>
      )}

      <button
        type="button"
        className={`report-back-to-top${showBackToTop ? ' report-back-to-top--visible' : ''}`}
        onClick={handleBackToTop}
        aria-label={tr('Back to top')}
        title={tr('Back to top')}
        tabIndex={showBackToTop ? 0 : -1}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 19V5M6.5 10.5 12 5l5.5 5.5" />
        </svg>
      </button>
    </div>
  );
};

export default ReportViewer;
